/*
    user context for MarTrak

    This context manages user authentication state using httpOnly cookies.
    The JWT token is stored in a secure httpOnly cookie (managed by the server).
    User data, permissions, and groups are stored in React state only (memory).

    On page refresh, initializeAuth() calls /auth/me to restore the session.
*/

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getMeAPI, logoutAPI } from "../services/authAPI"
import { useError } from './ErrorContext';
import { useSidebar } from './SidebarContext';

const UserContext = createContext();

/**
 * Custom hook to access the user context
 *
 * @returns {Object} The user context
 * @throws Will throw an error if the hook is used outside of a UserProvider.
 *
 * @category Context
 */
export const useUser = () => useContext(UserContext);

/**
 * Provides app access to user context
 *
 * @param {React.ReactNode} props.children - children components to be wrapped by the provider
 *
 * @returns {JSX.Element}
 *
 * @category Context
 */
export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userPermissions, setUserPermissions] = useState({})
    const [userGroups, setUserGroups] = useState([])
    const [isInitializing, setIsInitializing] = useState(true);
    const [hasSpeedApprovalIndicator, setHasSpeedApprovalIndicator] = useState(false);

    const [storedJob, setStoredJob] = useState()

    const navigate = useNavigate()
    const location = useLocation()
    const {showError} = useError()
    const {setIsOpen} = useSidebar()

    const expiryIntervalRef = useRef()
    const expiryWarningShownRef = useRef(false);
    const sessionExpiresAtRef = useRef(null);


    // Initialize auth on mount - check if user has valid session via cookie
    const broadcastChannel = new BroadcastChannel("auth_channel");

    useEffect(() => {
        initializeAuth();

        //broadcast changes to other open tabs 
        broadcastChannel.onmessage = (event) => {

            //log out all tabs so user doesn't try to make unauthorized requests
            if (event.data?.type === "LOGOUT") {

                logout(false); //pass param so we don't broadcast logout event again
            }
        };

        return () => {
                if (expiryIntervalRef.current) {
                    clearInterval(expiryIntervalRef.current);
                }

            broadcastChannel.close()
        };
    }, []) //eslint-disable-line

    // Stop polling if user logs out
    useEffect(() => {
        if (!user && expiryIntervalRef.current) {
            clearInterval(expiryIntervalRef.current);
            expiryIntervalRef.current = null;
        }
    }, [user]);

    const startExpiryPolling = (expiresAt) => {
        // Clear any existing interval
        if (expiryIntervalRef.current) {
            clearInterval(expiryIntervalRef.current);
        }

        expiryIntervalRef.current = setInterval(() => {
            if (!expiresAt) return;

            const currentTime = Date.now();
            const timeRemaining = (expiresAt * 1000) - currentTime;

            if (timeRemaining <= 0) {
                showError("Your session has expired - please log in again");
                clearInterval(expiryIntervalRef.current);
                expiryWarningShownRef.current = false;
                logout();
            } else if (timeRemaining <= 300000 && !expiryWarningShownRef.current) {
                showError(
                    "Your session will expire in 5 minutes. Please save your work or log in again."
                );
                expiryWarningShownRef.current = true;
            }
        }, 60000);
    };

    const initializeAuth = async () => {
        try {
            const response = await getMeAPI();

            if (response.success && response.user) {
                setUser(response.user);
                setUserPermissions(response.permissions);
                // Backend returns role on user object, convert to groups array for compatibility
                const groups = response.groups || (response.user.role ? [response.user.role] : []);
                setUserGroups(groups);

                sessionExpiresAtRef.current = response.expiresAt || null;

                // Start polling for session expiry if we have an expiry time
                if (response.expiresAt) {
                    startExpiryPolling(response.expiresAt);
                }
            }
        } catch (error) {
            console.log('Session restore failed:', error);
        } finally {
            setIsInitializing(false);
        }
    };

    // Note: Redirect to login for protected routes is handled by ProtectedRoute component

    const checkIsAuthenticated = () => {
        if (!user) {
            return false;
        }
        // If we have an expiry time, check it
        if (sessionExpiresAtRef.current) {
            return (sessionExpiresAtRef.current * 1000) > Date.now();
        }
        // No expiry time set - assume authenticated if we have a user
        return true;
    }

    // Login - called after successful API login, fetches user data from /auth/me
    // Returns { success: true/false, message?: string } so caller can handle errors
    const login = async () => {
        // Make sure sidebar set to closed, since home page has menu already
        setIsOpen(false);

        // Reset warning flag for new session
        expiryWarningShownRef.current = false;

        try {
            // Fetch user data from /auth/me (cookie is already set by login API)
            const response = await getMeAPI();

            if (!response.success || !response.user) {
                const errorMsg = response.message || "Failed to get user data after login";
                showError(errorMsg);
                return { success: false, message: errorMsg };
            }

            setUser(response.user);
            setUserPermissions(response.permissions);
            // Backend returns role on user object, convert to groups array for compatibility
            const groups = response.groups || (response.user.role ? [response.user.role] : []);
            setUserGroups(groups);

            sessionExpiresAtRef.current = response.expiresAt || null;

            if (response.expiresAt) {
                startExpiryPolling(response.expiresAt);
            }

            return { success: true };
        } catch (error) {
            const errorMsg = "Failed to complete login";
            showError(errorMsg);
            console.log('Login error:', error);
            return { success: false, message: errorMsg };
        }
    };

    // Logout - calls API to clear cookie, then clears local state
    // isBroadcast: whether to notify other tabs (default true, false when receiving broadcast)
    const logout = async (isBroadcast = true) => {
        // Clear intervals/refs
        if (expiryIntervalRef.current) {
            clearInterval(expiryIntervalRef.current);
            expiryIntervalRef.current = null;
        }
        expiryWarningShownRef.current = false;

        // Call logout API to clear the cookie
        try {
            await logoutAPI();
        } catch (error) {
            console.log('Logout API error:', error);
        }

        // Reset state
        setUser(null);
        setUserPermissions(null);
        setUserGroups(null);

        sessionExpiresAtRef.current = null

        if (!location.pathname.includes("login")) {
            navigate("/login");
        }

        if(isBroadcast){
            broadcastChannel.postMessage({ 
                type: "LOGOUT" 
            });
        }
    };

    /**
     * Turns speed approval indicator on or off.
     * 
     * Useage:
     *  - turn on when: active live track data comes in with items that have RequiresApproval = true
     *  - turn off when user visits speed approval page, or live track data has no RequiresApproval items (meaning someone approved/unapproved the speed)
     * 
     * @param {number} RunID 
     * @param {boolean} hasIndicator 
     * @param {number[]} speedApproverUserIDs 
     */
    const updatePendingSpeedApprovals = (RunID, hasIndicator, speedApproverUserIDs) => {

        setHasSpeedApprovalIndicator(prev => {
            // If no RunID provided -> clear all indicators
            // RunIDs will be available on pages with live track data, but not on speed approval page
            // if user goes to speed approval page, can remove indicator
            if (!RunID) {
                return {};
            }

            // Clearing a specific run (if speed approved for run, remove indicator)
            if (hasIndicator === false) {
                return {
                    ...prev,
                    [RunID]: false
                };
            }

            // if there are pending approvals, only set indicator if user is an approver
            if (
                hasIndicator === true &&
                speedApproverUserIDs &&
                user &&
                speedApproverUserIDs.includes(user.UserID)
            ) {
                return {
                    ...prev,
                    [RunID]: true
                };
            }

            return prev;
        });
    };

    const value = {
        user,
        login,
        logout,
        checkIsAuthenticated,
        userPermissions,
        userGroups,
        storedJob,
        setStoredJob,
        isInitializing,
        hasSpeedApprovalIndicator,
        updatePendingSpeedApprovals
    };

    // Show nothing while checking session (prevents flash of login page)
    if (isInitializing) {
        return null;
    }

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};
